/**
 * Client-side image cache for ImageElement rendering.
 *
 * The renderer runs inside a hot rAF loop and must be synchronous, but
 * image decoding is async. This cache sits between the two: callers ask
 * for a file by id, and they either get a ready HTMLImageElement back
 * immediately or `null` (in which case the renderer falls back to a
 * placeholder and comes back on the next repaint).
 *
 * Two ways a file enters the cache:
 *   1. **preload(fileId, url)** — called by the AssetLibrary when it
 *      lists the library so thumbnails start decoding before the DM
 *      drops them on the canvas.
 *   2. **resolve via urlProvider** — if an ImageElement references a
 *      fileId the renderer has never seen (e.g. the element came in via
 *      the scene sync before the library finished loading), the cache
 *      asks an async provider for the fresh presigned download URL and
 *      kicks off the load. Misses during the wait still render the
 *      placeholder.
 *
 * Loads notify a small set of listeners on success so the canvas can
 * schedule a repaint without polling.
 *
 * Cache is keyed by fileId (stable) not by URL (expires every ~30 min),
 * so a refreshed download URL for the same file reuses any already-
 * decoded `HTMLImageElement`.
 */

type Status = "idle" | "loading" | "ready" | "error";

interface Entry {
  status: Status;
  img: HTMLImageElement | null;
  /** Promise currently in flight for this entry (dedup). */
  loading?: Promise<void>;
}

type Listener = (fileId: string) => void;

export type UrlProvider = (fileId: string) => Promise<string | null>;

class ImageCache {
  private entries = new Map<string, Entry>();
  private listeners = new Set<Listener>();
  private urlProvider: UrlProvider | null = null;

  /** Register the async provider used when `get` misses on an unknown fileId. */
  setUrlProvider(provider: UrlProvider | null): void {
    this.urlProvider = provider;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(fileId: string): void {
    for (const l of [...this.listeners]) l(fileId);
  }

  /**
   * Pre-warm the cache with a known URL. Safe to call repeatedly — the
   * second call is a no-op unless the previous load failed (in which
   * case we retry with the new URL).
   */
  preload(fileId: string, url: string): void {
    const existing = this.entries.get(fileId);
    if (existing && (existing.status === "ready" || existing.status === "loading")) {
      return;
    }
    void this.load(fileId, url);
  }

  /**
   * Synchronous lookup used by the renderer. Returns a ready image
   * element, or null if the image is still loading / not yet started
   * (in which case the renderer draws a placeholder). If the entry is
   * missing entirely and we have a urlProvider, kicks off a fetch so a
   * later repaint can succeed.
   */
  get(fileId: string): HTMLImageElement | null {
    const entry = this.entries.get(fileId);
    if (entry?.status === "ready") return entry.img;
    if (!entry && this.urlProvider) {
      // Fire-and-forget: mark as loading, ask the provider for a URL,
      // then hand off to `load`. The caller gets null this frame.
      this.entries.set(fileId, { status: "loading", img: null });
      void this.urlProvider(fileId).then((url) => {
        if (!url) {
          this.entries.set(fileId, { status: "error", img: null });
          return;
        }
        return this.load(fileId, url);
      });
    }
    return null;
  }

  /** Drop the entry for a given fileId. Used when an asset is deleted. */
  invalidate(fileId: string): void {
    this.entries.delete(fileId);
  }

  private load(fileId: string, url: string): Promise<void> {
    const img = new Image();
    // crossOrigin is unnecessary here — we don't read pixels back, we
    // only composite via drawImage, which works tainted too.
    const existing = this.entries.get(fileId);
    if (existing?.loading) return existing.loading;

    const p = new Promise<void>((resolve) => {
      img.onload = () => {
        this.entries.set(fileId, { status: "ready", img });
        this.notify(fileId);
        resolve();
      };
      img.onerror = () => {
        this.entries.set(fileId, { status: "error", img: null });
        resolve();
      };
      img.src = url;
    });

    this.entries.set(fileId, { status: "loading", img: null, loading: p });
    return p;
  }
}

export const imageCache = new ImageCache();
